import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Table,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Input,
    Spinner,
    Label,
    FormGroup
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
    FaMoneyBillWave,
    FaPlus,
    FaEdit,
    FaSearch,
    FaCheckCircle,
    FaClock,
    FaTimes,
    FaFileInvoice,
    FaTruck,
    FaFilter,
    FaSync,
    FaTrashAlt
} from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';
import ReglementForm from '../ReglementClient/ReglementForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { reglementsFournisseurActions } from '../../sagas/reglementFournisseurSlice';
import Swal from 'sweetalert2';

const ReglementsFournisseurList = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { loading, error, list } = useSelector(state => state.reglementsFournisseur);
    const [showModal, setShowModal] = useState(false);
    const [selectedReglement, setSelectedReglement] = useState(null);
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const location = useLocation();
    const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);

    const [filters, setFilters] = useState({
        fournisseurId: null,
        dateFrom: formatDate(startOfMonth),
        dateTo: formatDate(endOfMonth),
        statut: null,
        query: ''
    });

    useEffect(() => {
        dispatch(reglementsFournisseurActions.listRequest(filters));
    }, [dispatch, filters]);


    const handleShowModal = () => {
        navigate("/purchases-module/payments/fournisseur/new");
    };

    const handleEdit = (reglement) => {
        navigate("/purchases-module/payments/fournisseur/edit", { state: { reglement } });
    };

    const handleSolderImpaye = (reglement) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmSync'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementsFournisseurActions.solderImpaye({
                    id: reglement.id,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.syncSuccess'),
                        });

                        handleReload();
                    }
                }));
            }
        });

    }

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReglement(null);
        handleReload(); // Refresh the filteredList instead of reloading the page
    };

    const handleReload = () => {
        dispatch(reglementsFournisseurActions.listRequest(filters));
    };

    const debouncedSearch = useCallback(
        debounce((query) => {
            dispatch(reglementsFournisseurActions.listRequest({
                ...filters,
                query: query
            }));
        }, 1500), // Wait 1.5 seconds after user stops typing
        [filters.query]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setFilters(prevFilters => ({
            ...prevFilters,
            query: value
        }));
        debouncedSearch(value);
    };

    const getStatutBadge = (statut) => {
        const badges = {
            'EN_ATTENTE': { color: 'info', icon: FaClock },
            'ENCAISSE': { color: 'success', icon: FaCheckCircle },
            'IMPAYE': { color: 'danger', icon: FaTimes },
            'ANNULE': { color: 'danger', icon: FaTimes }
        };

        const badge = badges[statut] || { color: 'secondary', icon: FaClock };
        const Icon = badge.icon;

        return (
            <Badge color={badge.color}>
                <Icon className="me-1" size="12" />
                {t(`reglement.${statut.toLowerCase()}`)}
            </Badge>
        );
    };

    const getModeReglementIcon = (mode) => {
        const icons = {
            'ESPECES': '💵',
            'CHEQUE': '📝',
            'VIREMENT': '🏦',
            'CARTE_BANCAIRE': '💳',
            'TRAITE': '📄',
            'AUTRE': '❓'
        };
        return icons[mode] || '❓';
    };

    const clearFilters = () => {
        setFilters({
            query: '',
            statut: '',
            clientId: null,
            dateFrom: formatDate(startOfMonth),
            dateTo: formatDate(endOfMonth)
        });
    };

    const handleAnnulerReglement = (reglement) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmAnnulation'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementsFournisseurActions.annulerReglement({
                    id: reglement.id,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.annulationSuccess'),
                        });
                        handleReload(); // Refresh the filteredList
                    }
                }));
            }
        });
    }

    // Calculate statistics
    const totalReglements = !loading ? list.length : 0;
    const totalMontant = !loading ? list.filter(r => r.typeReglement !== 'CAUTION').reduce((sum, r) => sum + (r?.montantRegle || 0), 0) : 0;
    const numberOfEnAttente = !loading ? list.filter(r => r.statut === 'EN_ATTENTE' && r.typeReglement !== 'CAUTION').length : 0;
    const totalMontantEnAttente = !loading ? list.filter(r => r.statut === 'EN_ATTENTE' && r.typeReglement !== 'CAUTION').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
    const totalDecaisse = !loading ? list.filter(r => r.statut === 'DECAISSE').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
    const numberOfDecaisse = !loading ? list.filter(r => r.statut === 'DECAISSE').length : 0;
    const totalCaution = !loading ? list.filter(r => r.typeReglement === 'CAUTION').reduce((sum, r) => sum + (r.montantRegle || 0), 0) : 0;
    const reglementsCaution = !loading ? list.filter(r => r.typeReglement === 'CAUTION').length : 0;
    const navigate = useNavigate();


    const handleDecaisserReglement = (reglement) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmDecaissement'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementsFournisseurActions.decaisserReglement({
                    id: reglement.id,
                    dateDecaissement: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.decaissementSuccess'),
                        });

                        handleReload(); // Refresh the filteredList
                    }
                }));
            }
        });
    };

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaMoneyBillWave className="text-primary me-2" />
                                {t('reglement.listFournisseur')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="primary" onClick={handleShowModal}>
                                    <FaPlus className="me-2" />
                                    {t('reglement.new')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Statistics Cards */}
            <Row className="mb-4">
                <Col lg={3} md={6} sm={6} xs={12} className="mb-3">
                    <Card className="shadow-sm bg-gradient h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                        {t('reglement.totalReglements')}
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{totalReglements}</div>
                                    <div className="text-xs text-primary mt-2">
                                        {formatPrice(totalMontant)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                                    <FaMoneyBillWave className="text-primary h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12} className="mb-3">
                    <Card className="shadow-sm bg-gradient h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                                        {t('reglement.enAttente')}
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{numberOfEnAttente}</div>
                                    <div className="text-xs text-warning mt-2">
                                        {formatPrice(totalMontantEnAttente)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                                    <FaClock className="text-warning h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12} className="mb-3">
                    <Card className="shadow-sm bg-gradient h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                                        {t('reglement.totalDecaisse')}
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{numberOfDecaisse}</div>
                                    <div className="text-xs text-success mt-2">
                                        {formatPrice(totalDecaisse)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-success bg-opacity-10 p-3">
                                    <FaCheckCircle className="text-success h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12} className="mb-3">
                    <Card className="shadow-sm bg-gradient h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                                        {t('reglement.caution')}
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{reglementsCaution}</div>
                                    <div className="text-xs text-info mt-2">
                                        {formatPrice(totalCaution)}
                                    </div>
                                </div>
                                <div className="rounded-circle bg-info bg-opacity-10 p-3">
                                    <FaMoneyBillWave className="text-info h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>



            {/* Filters */}
            <Card className="shadow-sm">
                <CardBody>
                    <Row className="mb-3 g-2 align-items-end">
                        <Col lg={5} md={4} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.search')}</Label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <Input
                                        type="text"
                                        placeholder={t('reglement.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilters(prevFilters => ({
                                                ...prevFilters,
                                                query: value
                                            }));
                                            debouncedSearch(value);
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.statut')}</Label>
                                <Input
                                    type="select"
                                    value={filters.statut}
                                    onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                                >
                                    <option value="">{t('reglement.allStatuts')}</option>
                                    <option value="EN_ATTENTE">{t('reglement.enAttente')}</option>
                                    <option value="ENCAISSE">{t('reglement.encaisse')}</option>
                                    <option value="ANNULE">{t('reglement.annule')}</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-3"
                                style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Card className="shadow-sm mt-4">
                <CardBody>
                    <Row>
                        <Col>

                            {loading ? (
                                <div className="text-center my-4"><Spinner color="primary" /></div>
                            ) : error ? (
                                <div className="text-danger">Erreur lors du chargement des réglements.</div>
                            ) : (
                                <table className="table table-hover table-striped table-bordered" style={{ fontSize: '0.8rem' }}>
                                    <thead className="thead-light">
                                        <tr>
                                            <th>{t('reglement.reference')}</th>
                                            <th>{t('reglement.fournisseur')}</th>
                                            <th>{t('reglement.documents')}</th>
                                            <th className="align-middle text-center">{t('reglement.type')}</th>
                                            <th>{t('reglement.dateReglement')}</th>
                                            <th>{t('reglement.montantRegle')}</th>
                                            <th>{t('reglement.modeReglement')}</th>
                                            <th>{t('reglement.statut')}</th>
                                            <th>{t('reglement.dateEncaissement')}</th>
                                            <th>{t('reglement.dateEcheance')}</th>
                                            <th>{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((reglement) => {

                                            return (
                                                <tr key={reglement.id}>
                                                    <td>
                                                        <strong>{reglement.reference || reglement.id}</strong>
                                                        {reglement.referencePaiement && (
                                                            <div className="text-muted small">
                                                                {reglement.referencePaiement}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {reglement.nomFournisseur}
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        {reglement.estAvance || reglement.typeReglement === 'CAUTION' ? (
                                                            <div>
                                                                <Badge color="info" className="mb-1">
                                                                    N/A
                                                                </Badge>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                {reglement.numFactures && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        {reglement.numFactures.split(',').map((invoice, index) => (
                                                                            <Badge key={index} color="primary" className='mb-1' style={{ fontSize: '10px' }}>
                                                                                {invoice.trim()}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {reglement.numBonReceptions && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        {reglement.numBonReceptions.split(',').map((receipt, index) => (
                                                                            <Badge key={index} color="success" className='mb-1' style={{ fontSize: '10px' }}>
                                                                                {receipt.trim()}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        {reglement.estAvance || reglement.typeReglement === 'AVANCE' ? (
                                                            <Badge color="warning">
                                                                <FaMoneyBillWave className="me-1" />
                                                                {t('reglement.avance')}
                                                            </Badge>
                                                        ) : reglement.numFactures ? (
                                                            <Badge color="primary">
                                                                <FaFileInvoice className="me-1" />
                                                                {t('reglement.factures')}
                                                            </Badge>
                                                        ) : reglement.numBonReceptions ? (
                                                            <Badge color="success">
                                                                <FaTruck className="me-1" />
                                                                {t('reglement.bonReceptions')}
                                                            </Badge>
                                                        ) : reglement.typeReglement === 'CAUTION' ? (
                                                            <Badge color="info">
                                                                <FaMoneyBillWave className="me-1" />
                                                                {t('reglement.caution')}
                                                            </Badge>) : (
                                                            <Badge color="secondary">
                                                                {t('reglement.autre')}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {new Date(reglement.dateReglement).toLocaleDateString('fr-FR')}
                                                    </td>
                                                    <td className="text-end">
                                                        <strong>{formatPrice(reglement.montantRegle)}</strong>
                                                        {reglement.montantAffecte > 0 && (
                                                            <div className="text-muted small">
                                                                {t('reglement.affecte')}: {formatPrice(reglement.montantAffecte)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="me-1">
                                                            {getModeReglementIcon(reglement.modeReglement)}
                                                        </span>
                                                        {t(`reglement.${reglement.modeReglement.toLowerCase()}`)}
                                                        {reglement.banque && (
                                                            <div className="text-muted small">{reglement.numeroCheque}
                                                                <br />
                                                                {reglement.banque}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{getStatutBadge(reglement.statut)}</td>
                                                    <td>
                                                        {reglement.dateDecaissement
                                                            ? new Date(reglement.dateDecaissement).toLocaleDateString('fr-FR')
                                                            : '-'
                                                        }
                                                    </td>
                                                    <td>
                                                        {reglement.dateEcheance
                                                            ? new Date(reglement.dateEcheance).toLocaleDateString('fr-FR')
                                                            : '-'
                                                        }
                                                    </td>
                                                    <td>
                                                        <div className="btn-group">
                                                            {(reglement.statut === 'EN_ATTENTE') && (
                                                                <Button
                                                                    color="danger"
                                                                    title={t('reglement.annuler')}
                                                                    size="sm"
                                                                    className="me-1"
                                                                    onClick={() => handleAnnulerReglement(reglement)}
                                                                >
                                                                    <FaTrashAlt size={12} />
                                                                </Button>
                                                            )}
                                                            {reglement.typeReglement != 'CAUTION' && (
                                                                <Button
                                                                    color="success"
                                                                    title={t('reglement.decaisser')}
                                                                    onClick={() => handleDecaisserReglement(reglement)}
                                                                    size="sm"
                                                                    className="me-1"
                                                                    disabled={reglement.statut === 'DECAISSE' || reglement.statut === 'ANNULE'}
                                                                >
                                                                    <FaCheckCircle size={12} />
                                                                </Button>
                                                            )
                                                            }
                                                            {/* Bouton Éditer */}
                                                            <Button
                                                                color="warning"
                                                                title={t('reglement.edit')}
                                                                onClick={() => handleEdit(reglement)}
                                                                size="sm"
                                                                className="me-1"
                                                            >
                                                                <FaEdit size={12} />
                                                            </Button>
                                                            {reglement.typeReglement != 'CAUTION' && reglement.statut === 'DECAISSE' && reglement.montantRegle > reglement.montantAffecte && (
                                                                <Button
                                                                    color="info"
                                                                    title={t('reglement.solder')}
                                                                    onClick={() => handleSolderImpaye(reglement)}
                                                                    size="sm"
                                                                    className="me-1"
                                                                    disabled={reglement.statut === 'ANNULE'}
                                                                >
                                                                    <FaSync size={12} />
                                                                </Button>
                                                            )
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {list.length === 0 && (
                                            <tr>
                                                <td colSpan="12" className="text-center py-4">
                                                    <div className="text-muted">{t('reglement.noReglements')}</div>
                                                </td>
                                            </tr>)
                                        }
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light fw-bold">
                                            <td colSpan="5" className="text-end">{t('common.totals')}</td>
                                            <td className="text-end">{formatPrice(list.reduce((sum, r) => sum + (r?.montantRegle || 0), 0))}</td>
                                            <td colSpan="6"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            )}

                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Modal for Create/Edit */}
            <Modal
                isOpen={showModal}
                toggle={handleCloseModal}
                size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseModal}>
                    {selectedReglement
                        ? `${t('reglement.edit')} - ${selectedReglement.reference || selectedReglement.id}`
                        : t('reglement.new')
                    }
                </ModalHeader>
                <ModalBody>
                    <ReglementForm
                        reglement={selectedReglement}
                        onClose={handleCloseModal}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default ReglementsFournisseurList;


